import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState, useMemo } from 'react';
import { Dispatch } from 'redux';
import { createContainer } from 'unstated-next';
import useRouter from 'use-react-router';
import { compile, match } from 'path-to-regexp';
import {
  selectCurrentUserId,
  selectSessionSelector,
} from '@mm/state/selectors/userSelector';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import usersCRUDActions from '@mm/state/actions/UsersCRUDActions';
import { getRoleFlagsByCode } from '@mm/state/model/CompanyUsersModel';
import { StoreState } from '@mm/state/store-interfaces';
import {
  BotBuilderRoutes,
  LIVE_CHAT_PATH,
} from '../../routes/bot-builder-routes';
import {
  CHATBOT_EDITOR,
  LOGIN,
  SIGNOUT,
  SETTINGS_MY_ACCOUNT,
  REFRESH,
} from '../constants/routes';
import { ErrorReportingUtils } from '@mm/plugins';
import { Routes } from '../../routes/top-level-routes';
import { isEqual, pick } from 'lodash';
import AuthenticatedController from '@mm/state/controllers/AuthenticatedController';

const FIELDS_TO_COMPARE_FOR_SESSION_UPDATE = [
  'userAttributes',
  'sessionsCount',
];

const useCurrentUserRole = () => {
  const [userRoleCode, setUserRoleCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const dispatch: Dispatch<StoreState> = useDispatch();

  const currentUserId = useSelector(selectCurrentUserId);
  const activeCompanyId = useSelector(selectActiveCompanyId);

  const session = useSelector(selectSessionSelector);

  useEffect(() => {
    const fetchCompanyUserView = async (
      companyId?: number,
      userId?: number,
    ) => {
      if (companyId && userId) {
        setIsLoading(true);
        try {
          const res = await dispatch(
            usersCRUDActions.retrieveById(companyId, userId),
          );

          // Note: our Session data is currently mostly updated on login,
          // while we need to use more recent data (e.g. sessionsCount) without making too many requests to BE.
          // So I'm addinng it here to update the session data on initial load/reload.
          const sessionDataCurrent = pick(
            session,
            FIELDS_TO_COMPARE_FOR_SESSION_UPDATE,
          );
          const sessionDataNew = pick(
            res,
            FIELDS_TO_COMPARE_FOR_SESSION_UPDATE,
          );

          if (!isEqual(sessionDataCurrent, sessionDataNew)) {
            await AuthenticatedController.storeSession({
              ...session,
              ...sessionDataNew,
            });
          }

          if (!res) {
            ErrorReportingUtils.reportError(
              'use-route-guard:fetchCompanyUserView',
              {
                extraData: { response: res },
              },
            );
          }
          const { roleCode } = res || {};
          setUserRoleCode(String(roleCode));
        } catch (err) {
          ErrorReportingUtils.reportError(
            'use-route-guard:fetchCompanyUserView',
            {
              extraData: err,
            },
          );
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    fetchCompanyUserView(activeCompanyId, currentUserId);
    if (!currentUserId) {
      setUserRoleCode('');
    }
  }, [currentUserId, activeCompanyId]);

  return useMemo(
    () => ({
      currentUserId,
      isLoading,
      roleFlags: getRoleFlagsByCode(userRoleCode),
    }),
    [currentUserId, isLoading, userRoleCode, getRoleFlagsByCode],
  );
};

export const UserRoleContainer = createContainer(useCurrentUserRole);

export const useRouteGuard = () => {
  const { roleFlags, currentUserId, isLoading } =
    UserRoleContainer.useContainer();
  const {
    history,
    location: { pathname },
  } = useRouter();

  const chatBotEditorLiveChatPath = LIVE_CHAT_PATH;

  const baseAllowedRoutes = [
    LOGIN,
    SIGNOUT,
    CHATBOT_EDITOR,
    `${Routes.freeLeadGenerator.path}(.*)`,
    `${Routes.automatedResponseTool.path}(.*)`,
    BotBuilderRoutes.dashboard.path, // allow match to start fetching bot data
  ];

  const liveAgentRoutes = [
    ...baseAllowedRoutes,
    `${chatBotEditorLiveChatPath}(.*)`,
    SETTINGS_MY_ACCOUNT,
  ];

  const monitorLeadsRoutes = [
    ...baseAllowedRoutes,
    BotBuilderRoutes.contacts.path,
    BotBuilderRoutes.audiences.path,
    SETTINGS_MY_ACCOUNT,
  ];

  function checkCurrentRoute(
    allowedPaths: string[],
    indexRouteAfterLogin?: string,
  ) {
    if (!isLoading && currentUserId) {
      const indexRouteMatch = match<{ chatbotId: string }>(
        BotBuilderRoutes.dashboard.path,
      )(pathname);

      if (indexRouteMatch && indexRouteAfterLogin) {
        const {
          params: { chatbotId },
        } = indexRouteMatch;
        history.push(compile(indexRouteAfterLogin)({ chatbotId }));
        return;
      }

      const isAuthorised = allowedPaths.some(
        path => match(path)(pathname) || match(`${REFRESH}${path}`)(pathname),
      );

      if (!isAuthorised) {
        // the route bellow redirects to specific bot available for user
        history.push(CHATBOT_EDITOR);
      }
    }
  }

  useEffect(() => {
    if (roleFlags.isLiveChatAgent) {
      checkCurrentRoute(liveAgentRoutes, chatBotEditorLiveChatPath);
    }
    if (roleFlags.isMonitorLeadsRole) {
      checkCurrentRoute(monitorLeadsRoutes, BotBuilderRoutes.contacts.path);
    }
  }, [
    isLoading, // handle route check for initial load & active company view switch (role isLoading)
    history.location.pathname, // handle route check for any location mutation (pathname)
    currentUserId, // check for session end
    roleFlags.isLiveChatAgent,
    roleFlags.isMonitorLeadsRole,
  ]);
};
